import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight, ChartColumn, ChevronDown, ChevronUp, Circle, CircleCheck, Info, Inbox, Network, SlidersHorizontal, X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Toast } from '../../../../components/design-system/common/Toast';
import { useQuoteApi } from '../../../../common/hooks/use-quote-api.hook';
import { useBillingApi } from '../../../../common/hooks/use-billing-api.hook';
import { VendorPlan, PlanFeatures } from '../../../../modules/superadmin/our-vendors/models/vendor.model';
import ardealyMark from '../../../../assets/ardealy-mark.svg';

type Quality = 'low' | 'medium' | 'high';
type FeatureKey = keyof Omit<PlanFeatures, 'visualizationQuality'>;
interface VolumeField { key: string; label: string; unit: string; min: number; max: number; step: number; info: string }

interface ModuleDef {
  feature: FeatureKey;      // the toggle's primary feature
  title: string;
  desc: string;
  accent: string;           // per-product accent dot colour
  volumes: VolumeField[];
  quality?: boolean;        // Visualizer: quality selector + 3D sub-toggle
  extra?: { key: FeatureKey; label: string }; // Visualizer: 3D models
}

const MODULES: ModuleDef[] = [
  { feature: 'contentStudio', title: 'Content Studio', desc: 'AI photos & video for every product', accent: '#F59E0B',
    volumes: [{ key: 'monthlyCredits', label: 'Credits / month', unit: 'credits / month', min: 100, max: 50000, step: 100,
      info: 'AI generation credits. Each generated photo or video clip consumes one credit; unused credits do not roll over.' }],
    extra: { key: 'seamlessGeneration', label: 'Seamless textures' } },
  { feature: 'aiSpaceAnalysis', title: 'Visualizer', desc: 'In-room & AR visualization', accent: '#EF4444',
    volumes: [{ key: 'maxVisualizationsPerMonth', label: 'Visualizations / month', unit: 'visualizations / month', min: 100, max: 100000, step: 100,
      info: 'How many times shoppers can render your products in a room or in AR each month, across your whole store.' }],
    quality: true, extra: { key: 'model3dGeneration', label: '3D models' } },
  { feature: 'socialScheduler', title: 'Social Planner', desc: 'Schedule posts & ads', accent: '#3B82F6',
    volumes: [
      { key: 'maxSocialAccounts', label: 'Connected accounts', unit: 'accounts', min: 1, max: 25, step: 1,
        info: 'Number of social profiles (e.g. Instagram, Facebook, TikTok) you can connect and publish to.' },
      { key: 'maxScheduledPostsPerMonth', label: 'Scheduled posts / month', unit: 'posts / month', min: 20, max: 2000, step: 20,
        info: 'How many posts and ads you can schedule across all connected accounts per month.' },
    ] },
  { feature: 'emailAutomation', title: 'Email Marketing', desc: 'Campaigns, automations & leads', accent: '#16A34A',
    volumes: [
      { key: 'maxEmailsPerMonth', label: 'Emails / month', unit: 'emails / month', min: 1000, max: 500000, step: 1000,
        info: 'Total emails sent per month across campaigns and automated flows combined.' },
      { key: 'maxAutomationRules', label: 'Automations', unit: 'automations', min: 1, max: 200, step: 1,
        info: 'Number of active automated flows (e.g. welcome series, abandoned cart) you can run.' },
      { key: 'maxLeads', label: 'Leads', unit: 'leads', min: 100, max: 200000, step: 100,
        info: 'How many leads/contacts you can store and market to in your CRM.' },
    ] },
];

const INCLUDED_VOLUMES: VolumeField[] = [
  { key: 'maxProducts', label: 'Products', unit: 'products', min: 100, max: 100000, step: 100,
    info: 'Total products (across all variants) you can hold in your catalog.' },
  // Was keyed maxSpaces (room photos) — a vendor asking for seats got spaces.
  { key: 'maxSeats', label: 'Seats', unit: 'seats', min: 1, max: 100, step: 1,
    info: 'People who can be signed in to the backoffice at the same time, each on one device.' },
  { key: 'maxImportProducts', label: 'Bulk import cap', unit: 'products / import', min: 100, max: 100000, step: 100,
    info: 'The maximum number of products you can create or update in a single bulk import.' },
  { key: 'maxSyncFlowsPerDay', label: 'Sync flows / day', unit: 'syncs / day', min: 1, max: 500, step: 1,
    info: 'How many automatic catalog syncs with your store can run per day to keep products up to date.' },
];

interface AddonDef { id: string; title: string; desc: string; Icon: React.ElementType; feature?: FeatureKey; bundle?: FeatureKey[] }
const ADDONS: AddonDef[] = [
  { id: 'apiIntegration', feature: 'apiIntegration', title: 'Product integration', desc: 'Our team imports & sets up your catalog', Icon: Inbox },
  { id: 'advancedAnalytics', feature: 'advancedAnalytics', title: 'Advanced analytics', desc: 'Deeper dashboards & exports', Icon: ChartColumn },
  { id: 'catalogCustomization', bundle: ['customFields', 'categoryManagement', 'productCardCustomization'], title: 'Catalog customization', desc: 'Custom fields, categories & product cards', Icon: SlidersHorizontal },
];
/** Keys an add-on toggles (single feature, or every key in a bundle). */
const addonKeys = (a: AddonDef): FeatureKey[] => (a.bundle ? a.bundle : a.feature ? [a.feature] : []);

const ALL_VOLUME_FIELDS: VolumeField[] = [...MODULES.flatMap((m) => m.volumes), ...INCLUDED_VOLUMES];
const QUALITY_LABEL: Record<Quality, string> = { low: 'Low quality', medium: 'Medium quality', high: 'High quality' };
const group = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// Uppercase eyebrow used for every section label in the modal.
const sectionLabelClass = 'text-xs font-medium tracking-wider text-muted-foreground uppercase';

// A segmented row of options — selected reads as an ink outline, never a tint.
// Repeated for render quality and payment method, hence the local component.
const Segmented = <V extends string>({ value, options, onSelect, groupLabel, fullWidth }: {
  value: V;
  options: { value: V; label: string; ariaLabel: string }[];
  onSelect: (v: V) => void;
  groupLabel: string;
  fullWidth?: boolean;
}) => (
  <div role="group" aria-label={groupLabel} className={cn('flex gap-1.5', fullWidth && 'w-full')}>
    {options.map((o) => (
      <Button
        key={o.value}
        variant="outline"
        size="sm"
        aria-label={o.ariaLabel}
        aria-pressed={value === o.value}
        onClick={() => onSelect(o.value)}
        className={cn(fullWidth && 'flex-1', value === o.value && 'border-foreground')}
      >
        {o.label}
      </Button>
    ))}
  </div>
);

interface QuoteConfiguratorModalProps {
  plan: VendorPlan | null;
  onClose: () => void;
}

/**
 * Enterprise "Build your plan" configurator (mirrors the ardealy.com design): a
 * two-column modal — left, product modules with on/off toggles + expandable
 * volume controls and an ADD-ONS section; right, a live "Your pack" summary and
 * a "Request a quote" CTA. Submits via createQuoteRequest (records + notifies
 * sales; never changes the plan). Opened from the sales-tier plan card.
 */
export const QuoteConfiguratorModal: React.FC<QuoteConfiguratorModalProps> = ({ plan, onClose }) => {
  const { t } = useTranslation();
  const quoteApi = useQuoteApi();
  const billingApi = useBillingApi();
  const [cardFeePercent, setCardFeePercent] = useState(0);

  const open = !!plan;
  const [modules, setModules] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [volumes, setVolumes] = useState<Record<string, string>>({});
  const [quality, setQuality] = useState<Quality>('medium');
  const [paymentMethod, setPaymentMethod] = useState<'wire' | 'stripe'>('stripe');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  // Card surcharge % (super-admin configurable) — shown as a note when the vendor
  // picks card payment. Fetched once when the modal opens.
  useEffect(() => {
    if (!plan) return;
    billingApi.getBillingConfig().then((c) => setCardFeePercent(c?.cardFeePercent || 0)).catch(() => setCardFeePercent(0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan]);

  useEffect(() => {
    if (!plan) return;
    const seeded: Record<string, boolean> = {};
    MODULES.forEach((m) => {
      seeded[m.feature] = Boolean(plan.features?.[m.feature]);
      if (m.extra) seeded[m.extra.key] = Boolean(plan.features?.[m.extra.key]);
    });
    ADDONS.forEach((a) => addonKeys(a).forEach((k) => { seeded[k] = Boolean(plan.features?.[k]); }));
    setModules(seeded);
    setExpanded({});
    // Sliders always carry a value — seed from the plan's limit (clamped to the
    // control's range) or fall back to the minimum.
    const seededVols: Record<string, string> = {};
    ALL_VOLUME_FIELDS.forEach((v) => {
      const fromPlan = Number((plan.limits as any)?.[v.key]);
      const val = Number.isFinite(fromPlan) && fromPlan > 0 ? Math.min(Math.max(fromPlan, v.min), v.max) : v.min;
      seededVols[v.key] = String(val);
    });
    setVolumes(seededVols);
    setQuality((plan.features?.visualizationQuality as Quality) || 'medium');
    setPaymentMethod('stripe');
    setDone(false);
    setSubmitting(false);
  }, [plan]);

  const selectedCount = useMemo(() => Object.values(modules).filter(Boolean).length, [modules]);

  // Translation helpers — config holds English fallbacks; keys resolve per locale.
  const tModuleTitle = (m: ModuleDef) => t(`QUOTE.MODULE.${m.feature}.TITLE`, m.title);
  const tModuleDesc = (m: ModuleDef) => t(`QUOTE.MODULE.${m.feature}.DESC`, m.desc);
  const tVolLabel = (v: VolumeField) => t(`QUOTE.VOLUME.${v.key}.LABEL`, v.label);
  const tVolUnit = (v: VolumeField) => t(`QUOTE.VOLUME.${v.key}.UNIT`, v.unit);
  const tVolInfo = (v: VolumeField) => t(`QUOTE.VOLUME.${v.key}.INFO`, v.info);
  const tExtraLabel = (e: { key: FeatureKey; label: string }) => t(`QUOTE.EXTRA.${e.key}.LABEL`, e.label);
  const tAddonTitle = (a: AddonDef) => t(`QUOTE.ADDON.${a.id}.TITLE`, a.title);
  const tAddonDesc = (a: AddonDef) => t(`QUOTE.ADDON.${a.id}.DESC`, a.desc);
  const tQualityFull = (q: Quality) => t(`QUOTE.QUALITY_FULL.${q}`, QUALITY_LABEL[q]);

  const setVol = (key: string, v: string) => setVolumes((p) => ({ ...p, [key]: v }));
  const toggleModule = (key: string, on: boolean) => setModules((p) => ({ ...p, [key]: on }));
  const toggleExpand = (key: string) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const selectedFeatures = Object.keys(modules).filter((k) => modules[k]);
      // Only submit volumes for enabled modules (+ the always-included catalog).
      const activeVolumeKeys = new Set<string>([
        ...MODULES.filter((m) => modules[m.feature]).flatMap((m) => m.volumes.map((v) => v.key)),
        ...INCLUDED_VOLUMES.map((v) => v.key),
      ]);
      const parsedVolumes: Record<string, number> = {};
      activeVolumeKeys.forEach((key) => {
        const n = Number(volumes[key]);
        if (Number.isFinite(n) && n > 0) parsedVolumes[key] = n;
      });
      await quoteApi.createQuoteRequest({
        selectedFeatures,
        volumes: parsedVolumes,
        visualizationQuality: modules['aiSpaceAnalysis'] ? quality : undefined,
        planId: plan?._id,
        paymentMethod,
      });
      setDone(true);
    } catch {
      setToast({ msg: t('QUOTE.ERROR', 'Could not send your request. Please try again.'), type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // ── "Your pack" summary lines ───────────────────────────────────────────────
  const moduleSummary = (m: ModuleDef): string => {
    const parts: string[] = [];
    m.volumes.forEach((v) => { if (volumes[v.key]) parts.push(`${group(Number(volumes[v.key]))} ${tVolUnit(v)}`); });
    if (m.quality) {
      parts.push(tQualityFull(quality));
      parts.push(m.extra && modules[m.extra.key] ? t('QUOTE.SUMMARY.AR_3D', 'AR & 3D') : t('QUOTE.SUMMARY.AR', 'AR'));
    } else if (m.extra && modules[m.extra.key]) {
      parts.push(tExtraLabel(m.extra));
    }
    return parts.join(' · ') || t('QUOTE.SUMMARY.INCLUDED', 'Included');
  };
  const includedSummary = (): string => {
    const parts = INCLUDED_VOLUMES.filter((v) => volumes[v.key]).map((v) => `${group(Number(volumes[v.key]))} ${tVolUnit(v)}`);
    return parts.join(' · ') || t('QUOTE.SUMMARY.CATALOG_FALLBACK', 'Shared catalog & seats');
  };
  const enabledAddons = ADDONS.filter((a) => addonKeys(a).every((k) => modules[k]));

  // Brand mark (blackened logo) + per-product accent dot.
  const Mark: React.FC<{ accent?: string; Icon?: React.ElementType }> = ({ accent, Icon }) => (
    <div className="relative flex size-8 shrink-0 items-center justify-center bg-muted">
      {Icon ? (
        <Icon className="size-[18px]" />
      ) : (
        <img src={ardealyMark} alt="" className="size-[19px] brightness-0 dark:invert" />
      )}
      {accent && <span className="absolute top-1 end-1 size-1.5 rounded-[2px]" style={{ backgroundColor: accent }} />}
    </div>
  );

  // (i) explainer — hover/focus reveals the tooltip.
  const InfoTip: React.FC<{ text: string; label: string }> = ({ text, label }) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          aria-label={`${t('QUOTE.A11Y.ABOUT', 'About')} ${label}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex cursor-help items-center text-muted-foreground hover:text-foreground"
        >
          <Info className="size-[15px]" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px]">{text}</TooltipContent>
    </Tooltip>
  );

  const volumeGrid = (fields: VolumeField[]) => (
    <div className="flex flex-col gap-3">
      {fields.map((v) => {
        const val = Number(volumes[v.key] ?? v.min);
        return (
          <div key={v.key}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <span className="text-sm text-muted-foreground">{tVolLabel(v)}</span>
                <InfoTip text={tVolInfo(v)} label={tVolLabel(v)} />
              </div>
              <span className="text-base font-medium">{group(val)}</span>
            </div>
            {/* A native range input, not the Radix slider: it keeps real
                min/max/step form semantics and responds to change events (which
                is also how the jsdom tests drive it). */}
            <input
              type="range"
              className="w-full accent-foreground"
              value={val}
              min={v.min}
              max={v.max}
              step={v.step}
              aria-label={tVolLabel(v)}
              onChange={(e) => setVol(v.key, e.target.value)}
            />
            <div className="flex justify-between">
              <span className="text-xs text-muted-foreground">{group(v.min)}</span>
              <span className="text-xs text-muted-foreground">{group(v.max)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => { if (!next && !submitting) onClose(); }}>
        <DialogContent showCloseButton={false} className="max-h-[92vh] overflow-y-auto bg-background p-4 sm:max-w-5xl md:p-6">
          {/* Own close button so its label goes through i18n. */}
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label={t('QUOTE.A11Y.CLOSE', 'Close')}
            onClick={onClose}
            className="absolute top-2 end-2 z-10"
          >
            <X />
          </Button>

          <TooltipProvider>
            {/* Header */}
            <div className="mb-4 text-center md:mb-5">
              <p className={cn(sectionLabelClass, 'mb-1')}>{t('QUOTE.OVERLINE', 'BUILD YOUR PLAN')}</p>
              <DialogTitle className="font-heading mb-0.5 text-2xl font-medium tracking-tight">
                {t('QUOTE.TITLE', 'Tailor Ardealy to your catalog')}
              </DialogTitle>
              <p className="mx-auto max-w-[480px] text-sm text-muted-foreground">
                {t('QUOTE.SUBTITLE', 'Activate the products you need and set your volumes. Get a quote tailored to exactly what you use.')}
              </p>
            </div>

            {done ? (
              <div className="py-8 text-center" data-testid="quote-done">
                <CircleCheck className="mx-auto mb-3 size-11 text-success" />
                <p className="font-heading mb-1 text-2xl font-medium tracking-tight">
                  {t('QUOTE.DONE_HEADING', 'Thanks — we’ll be in touch')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('QUOTE.DONE_BODY', 'Our team has your request and will get back to you shortly with a tailored quote.')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[1.5fr_1fr] md:gap-5">
                {/* LEFT: modules + add-ons */}
                <div className="rounded-xl border bg-card p-3 md:p-5">
                  {MODULES.map((m, i) => (
                    <div key={m.feature} className={cn('flex flex-col py-2.5', i > 0 && 'border-t')}>
                      <div className="flex items-center gap-3">
                        <Mark accent={m.accent} />
                        <div className="min-w-0 flex-1">
                          <p className="text-base font-medium">{tModuleTitle(m)}</p>
                          <p className="text-sm text-muted-foreground">{tModuleDesc(m)}</p>
                        </div>
                        <Switch
                          checked={Boolean(modules[m.feature])}
                          onCheckedChange={(on) => toggleModule(m.feature, on)}
                          aria-label={tModuleTitle(m)}
                        />
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          aria-label={`${t('QUOTE.A11Y.CONFIGURE', 'Configure')} ${tModuleTitle(m)}`}
                          onClick={() => toggleExpand(m.feature)}
                        >
                          {expanded[m.feature] ? <ChevronUp className="text-muted-foreground" /> : <ChevronDown className="text-muted-foreground" />}
                        </Button>
                      </div>
                      {/* Content stays mounted while collapsed (the hidden attribute keeps
                          it out of the a11y tree), matching the old Collapse behaviour. */}
                      <div hidden={!expanded[m.feature]}>
                        <div className="pt-3 sm:ps-10">
                          {volumeGrid(m.volumes)}
                          {(m.quality || m.extra) && (
                            <div className="mt-4 flex flex-wrap items-center gap-4">
                              {m.quality && (
                                <Segmented
                                  value={quality}
                                  onSelect={setQuality}
                                  groupLabel={t('QUOTE.A11Y.QUALITY', 'Render quality')}
                                  options={[
                                    { value: 'low', label: t('QUOTE.QUALITY.LOW', 'Low'), ariaLabel: tQualityFull('low') },
                                    { value: 'medium', label: t('QUOTE.QUALITY.MEDIUM', 'Medium'), ariaLabel: tQualityFull('medium') },
                                    { value: 'high', label: t('QUOTE.QUALITY.HIGH', 'High'), ariaLabel: tQualityFull('high') },
                                  ]}
                                />
                              )}
                              {m.extra && (
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={Boolean(modules[m.extra.key])}
                                    onCheckedChange={(on) => toggleModule(m.extra!.key, on)}
                                    aria-label={tExtraLabel(m.extra)}
                                  />
                                  <span className="text-sm text-muted-foreground">{tExtraLabel(m.extra)}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Catalog & team — always included */}
                  <div className="flex flex-col border-t py-2.5">
                    <div className="flex items-center gap-3">
                      <Mark Icon={Network} />
                      <div className="min-w-0 flex-1">
                        <p className="text-base font-medium">{t('QUOTE.CATALOG.TITLE', 'Catalog & team')}</p>
                        <p className="text-sm text-muted-foreground">{t('QUOTE.CATALOG.DESC', 'Always included. Your shared catalog and seats.')}</p>
                      </div>
                      <Badge variant="secondary">{t('QUOTE.INCLUDED', 'INCLUDED')}</Badge>
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        aria-label={`${t('QUOTE.A11Y.CONFIGURE', 'Configure')} ${t('QUOTE.CATALOG.TITLE', 'Catalog & team')}`}
                        onClick={() => toggleExpand('__catalog')}
                      >
                        {expanded['__catalog'] ? <ChevronUp className="text-muted-foreground" /> : <ChevronDown className="text-muted-foreground" />}
                      </Button>
                    </div>
                    <div hidden={!expanded['__catalog']}>
                      <div className="pt-3 sm:ps-10">{volumeGrid(INCLUDED_VOLUMES)}</div>
                    </div>
                  </div>

                  {/* ADD-ONS */}
                  <p className={cn(sectionLabelClass, 'mt-4 mb-2')}>{t('QUOTE.ADDONS', 'ADD-ONS')}</p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {ADDONS.map((a) => {
                      const keys = addonKeys(a);
                      const on = keys.every((k) => modules[k]);
                      return (
                        <div
                          key={a.id}
                          role="button"
                          tabIndex={0}
                          aria-label={tAddonTitle(a)}
                          aria-pressed={on}
                          onClick={() => setModules((p) => { const next = { ...p }; keys.forEach((k) => { next[k] = !on; }); return next; })}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setModules((p) => { const next = { ...p }; keys.forEach((k) => { next[k] = !on; }); return next; }); }}
                          className={cn(
                            'flex cursor-pointer items-center gap-3 rounded-xl border bg-card p-3 transition-colors',
                            on ? 'border-foreground' : 'hover:border-foreground/40',
                          )}
                        >
                          <div className={cn('flex size-8 shrink-0 items-center justify-center', on ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground')}>
                            <a.Icon className="size-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium">{tAddonTitle(a)}</p>
                            <p className="text-xs text-muted-foreground">{tAddonDesc(a)}</p>
                          </div>
                          {on ? <CircleCheck className="size-[18px] shrink-0" /> : <Circle className="size-[18px] shrink-0 text-border" />}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* RIGHT: Your pack */}
                <div className="rounded-xl border bg-card p-4 md:sticky md:top-4 md:p-5">
                  <p className="font-heading text-xl font-medium tracking-tight">{t('QUOTE.PACK', 'Your pack')}</p>
                  <p className="mb-3.5 text-sm text-muted-foreground">
                    {t('QUOTE.PACK_SUB', 'Tailored to what you select. We quote it for you.')}
                  </p>

                  {MODULES.filter((m) => modules[m.feature]).map((m) => (
                    <div key={m.feature} className="mb-2.5">
                      <p className={sectionLabelClass}>{tModuleTitle(m).toUpperCase()}</p>
                      <p className="text-sm">{moduleSummary(m)}</p>
                    </div>
                  ))}

                  <div className="mb-2.5">
                    <p className={sectionLabelClass}>{t('QUOTE.CATALOG.TITLE', 'Catalog & team').toUpperCase()}</p>
                    <p className="text-sm">{includedSummary()}</p>
                  </div>

                  {enabledAddons.length > 0 && (
                    <div className="mb-2.5">
                      <p className={sectionLabelClass}>{t('QUOTE.ADDONS', 'ADD-ONS')}</p>
                      <p className="text-sm">{enabledAddons.map((a) => tAddonTitle(a)).join(', ')}</p>
                    </div>
                  )}

                  {/* Payment method */}
                  <p className={cn(sectionLabelClass, 'mt-3.5 mb-1.5')}>{t('QUOTE.PAYMENT', 'Preferred payment')}</p>
                  <div className="mb-3.5">
                    <Segmented
                      fullWidth
                      value={paymentMethod}
                      onSelect={setPaymentMethod}
                      groupLabel={t('QUOTE.A11Y.PAYMENT', 'Preferred payment method')}
                      options={[
                        { value: 'stripe', label: t('QUOTE.PAY_CARD', 'Card (Stripe)'), ariaLabel: t('QUOTE.A11Y.PAY_CARD', 'Card / Stripe') },
                        { value: 'wire', label: t('QUOTE.PAY_WIRE', 'Bank transfer'), ariaLabel: t('QUOTE.A11Y.PAY_WIRE', 'Wire transfer') },
                      ]}
                    />
                  </div>

                  {paymentMethod === 'stripe' && cardFeePercent > 0 && (
                    <p className="mb-3.5 text-xs leading-relaxed text-muted-foreground">
                      {t('QUOTE.CARD_FEE_NOTE', 'Card payments add a {{pct}}% processing fee to cover our payment provider’s costs. Choose bank transfer to avoid it.', { pct: cardFeePercent })}
                    </p>
                  )}

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleSubmit}
                    disabled={submitting || selectedCount === 0}
                  >
                    {submitting ? t('QUOTE.SENDING', 'Sending…') : t('QUOTE.SUBMIT', 'Request a quote')}
                    <ArrowRight />
                  </Button>
                  <p className="mt-2 text-center text-xs text-muted-foreground">
                    {t('QUOTE.FINEPRINT', 'No price upfront · No commitment · Reply within 24h')}
                  </p>
                </div>
              </div>
            )}
          </TooltipProvider>
        </DialogContent>
      </Dialog>

      <Toast message={toast?.msg || ''} type={toast?.type || 'error'} isVisible={!!toast} onClose={() => setToast(null)} />
    </>
  );
};

export default QuoteConfiguratorModal;
